/**
 * The setup path — which workflow a new property is put on, and when it is
 * finished with it.
 *
 * Client-safe on purpose. The chip on the organization page, the companion and
 * the sidebar all ask "is this property tracking yet", and all three have to
 * answer the same way as the server that stamps `trackingReadyAt`. Everything
 * here is a pure read of the observed state the workflow engine already loads.
 *
 * Server-side counterparts (stamping the column, starting the run) live in
 * `setup-server.ts`, which reads the database.
 */

import type { SubjectType } from "../types/property";
import type { WorkflowPropertyState } from "./state";

/**
 * The setup workflow for a subject with a focal brand of its own. W2.
 *
 * Still exported under its old name because it is the default, and because
 * several surfaces want the BRAND checklist by name. Anything that has a
 * subject type in hand should call `setupWorkflowIdFor` instead.
 */
export const SETUP_WORKFLOW_ID = "visibility-tracking";

/**
 * The setup workflow for a market. W32.
 *
 * Same shape as W2 and the same first move: start the report. A market has no
 * focal brand, so its report writes the first prompt set itself when there is
 * none, and the brands come out of the answers rather than being named first.
 * W32 is W2 with the brand wording replaced and the brand review moved ahead of
 * the prompt step, because the report is what fills the brand list.
 */
export const CATEGORY_SETUP_WORKFLOW_ID = "category-tracking";

/** Both setup checklists, for the reads that look for either one. */
export const SETUP_WORKFLOW_IDS = [
	SETUP_WORKFLOW_ID,
	CATEGORY_SETUP_WORKFLOW_ID,
] as const;

/** Which setup checklist this subject belongs on. */
export function setupWorkflowIdFor(
	subjectType: SubjectType | string | null | undefined,
): string {
	return subjectType === "CATEGORY"
		? CATEGORY_SETUP_WORKFLOW_ID
		: SETUP_WORKFLOW_ID;
}

/**
 * Is this one of the setup checklists?
 *
 * For the surfaces that go looking for "the checklist this property was put on
 * when it was created" and have a list of active workflows rather than a
 * subject type. Asking by id covers both without a second lookup.
 */
export function isSetupWorkflowId(workflowId: string): boolean {
	return (SETUP_WORKFLOW_IDS as readonly string[]).includes(workflowId);
}

/**
 * The retention definition of a set-up property: prompts are running daily and
 * the work is pointed at something.
 *
 * Any goal on an active project counts. Both routes through the product end in
 * a real answer to "what is this tracking for": an SEO team points a goal at a
 * group of its pages, a PR team points one at a coverage group, a publisher, an
 * article or an author. Holding out for a page group or a coverage group only
 * told the PR half of the audience that the work it had just set up did not
 * count.
 */
export function isTrackingReady(
	state: Pick<
		WorkflowPropertyState,
		"activeProjectCount" | "activeProjectGoalCount"
	>,
): boolean {
	return state.activeProjectCount >= 1 && state.activeProjectGoalCount >= 1;
}

/**
 * Does this property get the setup workflow at all?
 *
 * Every subject type that gets a property home of its own, minus prospects. A
 * LOCATION child inherits its parent's setup and has no dashboard of its own,
 * and a prospect is a sales record nobody is going to set up. A CATEGORY does
 * get the walkthrough: it has a home, projects and a competitor breakout to
 * fill.
 */
export function isSetupEligible(property: {
	subjectType: SubjectType | string | null | undefined;
	isProspect?: boolean | null;
}): boolean {
	if (property.isProspect) {
		return false;
	}
	return (
		property.subjectType === "BRAND" ||
		property.subjectType === "PRODUCT" ||
		property.subjectType === "PERSON" ||
		property.subjectType === "CATEGORY"
	);
}
